import {
  Activity,
  Bot,
  CheckCircle2,
  CircleAlert,
  ReceiptText,
  TrendingUp,
} from 'lucide-react'

import { cn } from '@/lib/utils'

export function AppraisalOutputPanel({
  hasCalculated,
  selectedStrategy,
  result,
}) {
  const strategyName = selectedStrategyName(selectedStrategy)
  const valueAddLogic = Array.isArray(selectedStrategy?.valueAddLogic)
    ? selectedStrategy.valueAddLogic
    : []
  const metrics = selectedStrategy?.metrics ?? {}

  if (!hasCalculated) {
    return (
      <aside className="relative min-h-[34rem] xl:h-full">
        <section
          aria-label="Active strategy summary"
          className="z-10 rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm xl:sticky xl:top-6"
        >
          <PreviewOutput
            strategyName={strategyName}
            valueAddLogic={valueAddLogic}
            metrics={metrics}
          />
        </section>
        <div className="mt-8 space-y-4 opacity-20 blur-[2px] grayscale">
          <LockedOutputScaffold />
        </div>
      </aside>
    )
  }

  return (
    <aside className="space-y-4 rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm xl:sticky xl:top-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Appraisal Output
        </p>
        <h3 className="mt-2 text-lg font-semibold tracking-normal text-foreground">
          Appraisal result
        </h3>
      </div>

      {result ? (
        <CalculatedOutput result={result} />
      ) : (
        <LockedOutputScaffold />
      )}
    </aside>
  )
}

function PreviewOutput({ strategyName, valueAddLogic, metrics }) {
  if (!strategyName) {
    return (
      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <p className="text-sm leading-relaxed text-muted-foreground">
            No strategy selected. Choose a concept before calculating appraisal output.
          </p>
        </div>
      </div>
    )
  }

  const incomePotential = compactIncomePotential(metrics.incomePotential)
  const complexity = metrics.complexity ?? 'Medium'

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
          <Activity aria-hidden="true" className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="mb-1 inline-flex rounded bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
            Active Strategy
          </p>
          <h3 className="text-base font-bold leading-tight text-foreground">
            {strategyName}
          </h3>
        </div>
      </div>
      {valueAddLogic.length ? (
        <section className="border-t border-border/50 pt-4">
          <h4 className="mb-3 text-xs font-bold text-foreground">Value-Add Plan</h4>
          <ul className="space-y-2">
            {valueAddLogic.slice(0, 3).map((item) => (
              <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                <span aria-hidden="true" className="mt-2 h-1 w-1 rounded-full bg-primary" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className="flex items-center gap-2 border-t border-border/50 pt-4 text-xs">
        <span className="font-bold text-primary">{incomePotential}</span>
        <span className="text-muted-foreground">|</span>
        <span className="font-bold text-foreground">{complexity} complexity</span>
      </div>
    </div>
  )
}

function LockedOutputScaffold() {
  return (
    <>
      <div className="rounded-xl border border-primary/30 bg-primary/5 px-6 py-8 text-center shadow-sm">
        <h3 className="mb-3 text-[11px] font-bold uppercase tracking-widest text-primary">
          Maximum Purchase Price
        </h3>
        <p className="text-4xl font-black tracking-tight text-foreground">£--,---</p>
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
          <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
          Hits 20% Target ROCE
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-widest text-foreground">
          Suggested Offer Strategy
        </h3>
        <div className="mt-4 space-y-3">
          <MetricRow label="Initial Offer" value="£--,---" />
          <MetricRow label="Target Close" value="£--,---" isStrong />
          <MetricRow label="Walk Away" value="£--,---" />
        </div>
      </div>
    </>
  )
}

function CalculatedOutput({ result }) {
  return (
    <div className="space-y-4">
      <dl className="grid gap-3">
        <MetricCard label="Maximum Purchase Price" value={result.maxPrice} emphasis />
        <MetricCard label="Initial Offer" value={result.initialOffer} />
        <MetricCard label="Target Close" value={result.targetClose} />
        <MetricCard label="Walk Away" value={`${result.maxPrice} ceiling`} />
      </dl>

      <section className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
        <SignalCard icon={TrendingUp} label="ROCE" value={result.roce} />
        <SignalCard icon={Activity} label="Cashflow" value={result.cashflow} />
        <SignalCard icon={ReceiptText} label="Profit" value={result.profit} />
      </section>

      <OutputSection icon={ReceiptText} title="Cost breakdown">
        <div className="divide-y divide-border rounded-lg border border-border bg-background">
          {(result.costBreakdown ?? []).map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 p-3">
              <span className="text-sm text-muted-foreground">{row.label}</span>
              <span className="text-sm font-semibold text-foreground">{row.value}</span>
            </div>
          ))}
        </div>
      </OutputSection>

      <OutputSection icon={Activity} title="Sensitivity signals">
        <div className="space-y-2">
          {(result.sensitivitySignals ?? []).map((signal) => (
            <div
              key={signal.label}
              className="rounded-lg border border-border bg-background p-3"
            >
              <p className="text-sm font-semibold text-foreground">{signal.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{signal.value}</p>
            </div>
          ))}
        </div>
      </OutputSection>

      <OutputSection icon={Bot} title="Calculation note">
        <p className="rounded-lg border border-border bg-background p-3 text-sm leading-relaxed text-muted-foreground">
          {result.interpretation ?? 'Formula-based appraisal calculated from current manual assumptions.'}
        </p>
      </OutputSection>
    </div>
  )
}

function MetricCard({ label, value, emphasis = false }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'mt-1 font-semibold text-foreground',
          emphasis ? 'text-2xl font-bold' : 'text-base',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function MetricRow({ label, value, isStrong = false }) {
  return (
    <div className={cn('flex items-center justify-between rounded-lg p-3', isStrong && 'bg-muted/30')}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={cn('font-semibold text-foreground', isStrong && 'text-primary')}>
        {value}
      </span>
    </div>
  )
}

function compactIncomePotential(value) {
  if (!value) return '+£2k pcm'
  if (value.includes('+£2k')) return '+£2k pcm'
  if (value.includes('<£1k')) return '<£1k pcm'

  return value
}

function SignalCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <Icon className="mb-2 h-4 w-4 text-primary" />
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
    </div>
  )
}

function OutputSection({ icon: Icon, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      </div>
      {children}
    </section>
  )
}

function selectedStrategyName(selectedStrategy) {
  if (!selectedStrategy) return null
  if (typeof selectedStrategy === 'string') return selectedStrategy

  return (
    selectedStrategy.name ??
    selectedStrategy.title ??
    selectedStrategy.label ??
    selectedStrategy.id ??
    null
  )
}
